import {
  Component,
  Output,
  EventEmitter,
  ViewEncapsulation,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  AppDataSharingService,
  AppConfigService,
  MobileService
} from '@pulse/common';
import { OktaAuthService } from '../../core/oktaauth.service';
import { ThreeGradientMapDirective } from '../custom-directives/three-gradient-map.directive';
import { WebGLGradientDirective } from '../custom-directives/webgl-gradient.directive';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { Router } from '@angular/router';
import { AppLoadService } from '../../app-load.service';

@Component({
  selector: 'pulse-marketing',
  standalone: true,
  imports: [CommonModule, TranslateModule, ThreeGradientMapDirective, WebGLGradientDirective],
  templateUrl: './pulse-marketing.component.html',
  styleUrls: ['./pulse-marketing.component.scss', '../../../assets/css/loader.css'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class PulseMarketingComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() signInClicked = new EventEmitter<boolean>();
  @ViewChild('videoContainer') videoContainerRef!: ElementRef;

  userLoggedIn = false;
  showLoginModal = false;

  heroImageUrl = '/assets/images/hero.png';
  videoUrl: SafeResourceUrl;

  private videoObserver: IntersectionObserver | null = null;
  private hasVideoPlayed = false;
  private smoother: ScrollSmoother | null = null;
  private useNativeScroll = false;
  public isMobile: boolean = false;

  constructor(
    private oktaAuthService: OktaAuthService,
    private configService: AppConfigService,
    private sanitizer: DomSanitizer,
    private appLoadService: AppLoadService,
    private router: Router,
    private mobileService: MobileService,
    private host: ElementRef<HTMLElement>,
  ) {
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://cbre.qumucloud.com/view/GlobalPulseDemo?autoplay=0&mute=1&volume=0%27'
    );
  }

  ngOnInit() {
    this.isMobile = this.mobileService.isMobile ? true : false;
    this.useNativeScroll = this.isMobile;
    if (this.useNativeScroll) {
      this.host.nativeElement.classList.add('native-scroll-mode');
    }

    if (sessionStorage.getItem('userLoggedIn') || sessionStorage.getItem('userLoggedOut')) {
      this.userLoggedIn = true;
      sessionStorage.removeItem('userLoggedIn');
      sessionStorage.removeItem('userLoggedOut');
      this.oktaAuthService.initiateLogin();
    }

    if(this.appLoadService.isInvalidUser == true){
        this.router.navigate(['/unauthorized'])
      }

  }

  ngAfterViewInit(): void {
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother);
    setTimeout(() => this.initAnimations(), 100);

    // Intersection observer for video autoplay
    if (this.videoContainerRef) {
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.hasVideoPlayed) {
              this.hasVideoPlayed = true;
              this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
                'https://cbre.qumucloud.com/view/GlobalPulseDemo?autoplay=1'
              );
            }
          });
        },
        { threshold: 0.5 }
      );
      this.videoObserver.observe(this.videoContainerRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.videoObserver?.disconnect();
    this.smoother?.kill();
    ScrollTrigger.getAll().forEach(t => t.kill());
  }

  onSignIn() {
    sessionStorage.setItem('userLoggedIn', 'true');
    const authType = this.configService.get('authType');
    if (authType === 3) {
      this.oktaAuthService.initiateLogin();
    }
  }

  handleSSOLogin() {
    this.closeLoginModal();
    this.onSignIn();
  }

  openLoginModal() {
    this.showLoginModal = true;
  }

  closeLoginModal() {
    this.showLoginModal = false;
  }

  openExternalLink(event: MouseEvent, url: string) {
    event.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private initAnimations() {
    const root: ShadowRoot | HTMLElement =
      this.host.nativeElement.shadowRoot ?? this.host.nativeElement;

    // ── ScrollSmoother setup ─────────────────────────────────
    const smoothWrapper = root.querySelector<HTMLElement>('#smooth-wrapper');
    const smoothContent = root.querySelector<HTMLElement>('#smooth-content');
    if (smoothWrapper && smoothContent && !this.useNativeScroll) {
      this.smoother = ScrollSmoother.create({
        wrapper: smoothWrapper,
        content: smoothContent,
        smooth: 1.5,
        smoothTouch: 0.1,
        effects: true,
      });
    }

    gsap.context(() => {
      const header = root.querySelector<HTMLElement>('header');
      if (header) gsap.from(header, { duration: 0.8, y: -80, opacity: 0, ease: 'power3.out' });

      const heroTitle = root.querySelector('.hero-title');
      if (heroTitle) gsap.from(heroTitle, { duration: 1.2, y: 60, opacity: 0, ease: 'power4.out' });

      const heroSubtitle = root.querySelector('.hero-subtitle');
      if (heroSubtitle) gsap.from(heroSubtitle, { duration: 1, y: 40, opacity: 0, delay: 0.3, ease: 'power3.out' });

      if (this.useNativeScroll) {
        const visibleSelectors = [
          '.video-section-title',
          '.video-section-subtitle',
          '.video-container',
          '.pulse-do-title',
          '.tech-card',
          '.feature-card',
        ];
        const visibleElements = root.querySelectorAll(visibleSelectors.join(','));
        if (visibleElements.length > 0) {
          gsap.set(visibleElements, { opacity: 1, y: 0, scale: 1, clearProps: 'transform' });
        }
        return;
      }

      const videoTitle = root.querySelector('.video-section-title');
      if (videoTitle) gsap.from(videoTitle, { scrollTrigger: { trigger: videoTitle, start: 'top 80%', toggleActions: 'play none none reverse' }, duration: 0.8, y: 50, opacity: 0, ease: 'power3.out' });

      const videoSubtitle = root.querySelector('.video-section-subtitle');
      if (videoSubtitle) gsap.from(videoSubtitle, { scrollTrigger: { trigger: videoSubtitle, start: 'top 80%', toggleActions: 'play none none reverse' }, duration: 0.8, y: 50, opacity: 0, ease: 'power3.out' });

      const videoContainer = root.querySelector('.video-container');
      if (videoContainer) gsap.from(videoContainer, { scrollTrigger: { trigger: videoContainer, start: 'top 80%', toggleActions: 'play none none reverse' }, duration: 1, scale: 0.95, opacity: 0, ease: 'power3.out' });

      const pulseDoTitle = root.querySelector('.pulse-do-title');
      if (pulseDoTitle) gsap.from(pulseDoTitle, { scrollTrigger: { trigger: pulseDoTitle, start: 'top 80%', toggleActions: 'play none none reverse' }, duration: 0.6, y: 30, opacity: 0, ease: 'power3.out' });

      const techCards = root.querySelectorAll('.tech-card');
      if (techCards.length > 0) gsap.fromTo(techCards, { opacity: 0, y: 60 }, { scrollTrigger: { trigger: techCards[0], start: 'top 75%', toggleActions: 'play none none none' }, opacity: 1, y: 0, duration: 0.8, stagger: 0.2, ease: 'power3.out' });

      const featureCards = root.querySelectorAll('.feature-card');
      if (featureCards.length > 0) gsap.fromTo(featureCards, { opacity: 0, y: 50 }, { scrollTrigger: { trigger: featureCards[0], start: 'top 75%', toggleActions: 'play none none none' }, opacity: 1, y: 0, duration: 0.7, stagger: 0.15, ease: 'power3.out' });
    }, root);
  }

}